import React, { useRef, useState, useEffect, useCallback } from "react";
import { Mic, Square, Upload, Play, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";
import InfoDot from "@/studio/InfoDot";

function drawWaveform(canvas, buffer, color) {
  if (!canvas || !buffer) return;
  const ctx = canvas.getContext("2d");
  const w = (canvas.width = canvas.offsetWidth);
  const h = (canvas.height = canvas.offsetHeight);
  ctx.clearRect(0, 0, w, h);
  const data = buffer.getChannelData(0);
  const step = Math.ceil(data.length / w);
  const amp = h / 2;
  ctx.fillStyle = color;
  for (let i = 0; i < w; i++) {
    let min = 1.0;
    let max = -1.0;
    for (let j = 0; j < step; j++) {
      const d = data[i * step + j] || 0;
      if (d < min) min = d;
      if (d > max) max = d;
    }
    ctx.fillRect(i, (1 + min) * amp, 1, Math.max(1, (max - min) * amp));
  }
}

function TrackLane({ id, label, color, recTrack, setRecTrack, onRename, onRemove }) {
  const { engine, currentStep, timelineVersion } = useStudio();
  const canvasRef = useRef(null);
  const [hasClip, setHasClip] = useState(false);
  const [clipName, setClipName] = useState("");
  const [name, setName] = useState(label);
  const [dragOver, setDragOver] = useState(false);
  const recording = recTrack === id;

  // Reflect clips loaded into this track from elsewhere (e.g. "Send to Timeline" from Beat Lab)
  useEffect(() => {
    const buf = engine.started && engine.trackBuffers ? engine.trackBuffers[id] : null;
    if (buf && !hasClip) {
      setHasClip(true);
      setClipName((prev) => prev || "beat");
      setTimeout(() => drawWaveform(canvasRef.current, buf, color), 60);
    }
  }, [engine, id, color, timelineVersion, hasClip]);

  const onBuffer = useCallback(
    (trackId, buffer) => {
      engine.setTrackBuffer(trackId, buffer);
      setHasClip(true);
      setTimeout(() => drawWaveform(canvasRef.current, buffer, color), 50);
    },
    [engine, color]
  );

  const startRec = async () => {
    if (recTrack && recTrack !== id) {
      toast.error("Another track is recording");
      return;
    }
    try {
      await engine.recordTrack(id, 0, onBuffer);
      setRecTrack(id);
      setClipName("live take");
      toast.info(`Recording ${label}…`);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  };

  const stopRec = () => {
    engine.stopRecordTrack();
    setRecTrack(null);
    toast.success(`Captured ${label}`);
  };

  const loadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio") && !/\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(file.name)) {
      toast.error("Please use an audio file (MP3, WAV, OGG…)");
      return;
    }
    try {
      engine.ensureStarted();
      const ab = await file.arrayBuffer();
      const buf = await engine.ctx.decodeAudioData(ab);
      engine.setTrackBuffer(id, buf);
      setHasClip(true);
      setClipName(file.name.slice(0, 20));
      setTimeout(() => drawWaveform(canvasRef.current, buf, color), 50);
      toast.success(`Imported to ${label}`);
    } catch (err) {
      toast.error("Could not decode file");
    }
  };
  const onFile = (e) => loadFile(e.target.files[0]);

  const clearClip = () => {
    engine.setTrackBuffer(id, null);
    setHasClip(false);
    setClipName("");
    const c = canvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  return (
    <div className="flex border-b border-[#262626]" style={{ height: 88 }} data-testid={`track-lane-${id}`}>
      {/* header */}
      <div className="w-40 shrink-0 bg-[#1a1a1a] border-r border-[#262626] p-2 flex flex-col justify-between">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
          <input
            data-testid={`track-name-${id}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              onRename(id, e.target.value);
            }}
            className="bg-transparent hw-label text-[9px] outline-none border-b border-transparent focus:border-[#404040] w-20 text-neutral-300"
          />
          <button
            data-testid={`remove-${id}`}
            onClick={() => onRemove(id)}
            className="ml-auto text-neutral-600 hover:text-[#ff3333] transition-colors"
            title="Delete track"
          >
            <X size={13} />
          </button>
        </div>
        <div className="flex items-center gap-1">
          {!recording ? (
            <button
              data-testid={`rec-${id}`}
              onClick={startRec}
              className="w-7 h-7 rounded-sm border border-[#404040] flex items-center justify-center hover:border-[#ff3333] transition-colors"
            >
              <Mic size={13} className="text-[#ff3333]" />
            </button>
          ) : (
            <button
              data-testid={`stop-rec-${id}`}
              onClick={stopRec}
              className="w-7 h-7 rounded-sm border border-[#ff3333] bg-[#ff3333]/20 flex items-center justify-center led-pulse"
            >
              <Square size={12} className="text-[#ff3333]" fill="#ff3333" />
            </button>
          )}
          <label className="w-7 h-7 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] cursor-pointer transition-colors">
            <Upload size={13} className="text-neutral-400" />
            <input type="file" accept="audio/*" className="hidden" onChange={onFile} data-testid={`import-${id}`} />
          </label>
          {hasClip && (
            <button
              data-testid={`clear-${id}`}
              onClick={clearClip}
              className="w-7 h-7 rounded-sm border border-[#404040] flex items-center justify-center hover:border-[#ff3333] transition-colors"
            >
              <Trash2 size={12} className="text-neutral-400" />
            </button>
          )}
        </div>
      </div>
      {/* clip region */}
      <div
        className="flex-1 relative bg-[#050505] transition-all"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          loadFile(e.dataTransfer.files[0]);
        }}
        style={dragOver ? { boxShadow: `inset 0 0 0 2px ${color}, inset 0 0 24px ${color}55` } : {}}
      >
        {/* bar grid */}
        <div className="absolute inset-0 flex pointer-events-none">
          {Array.from({ length: 16 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 border-r"
              style={{ borderColor: i % 4 === 3 ? "#222" : "#141414" }}
            />
          ))}
        </div>
        {dragOver && (
          <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
            <span className="hw-label text-[10px]" style={{ color }}>Drop audio to import</span>
          </div>
        )}
        {hasClip && (
          <div
            className="absolute inset-y-2 left-1 right-1 rounded-sm border overflow-hidden"
            style={{ borderColor: color, background: `${color}18` }}
          >
            <div className="absolute top-0 left-0 right-0 px-2 py-0.5 text-[8px] font-mono-data" style={{ color }}>
              {clipName}
            </div>
            <canvas ref={canvasRef} className="w-full h-full opacity-80" />
          </div>
        )}
        {/* playhead */}
        {currentStep >= 0 && (
          <div
            className="absolute top-0 bottom-0 w-px bg-white/80 pointer-events-none"
            style={{ left: `${((currentStep + 0.5) / 16) * 100}%`, boxShadow: "0 0 8px #fff" }}
          />
        )}
      </div>
    </div>
  );
}

export default function Timeline() {
  const { currentStep, engine, forceUpdate } = useStudio();
  const [recTrack, setRecTrack] = useState(null);

  const trackIds = engine.started
    ? Object.keys(engine.channels)
        .filter((id) => /^track\d+$/.test(id))
        .sort((a, b) => Number(a.slice(5)) - Number(b.slice(5)))
    : ["track1", "track2"];

  const addTrack = () => {
    engine.ensureStarted();
    engine.addTrack();
    forceUpdate();
  };

  const onRename = (id, name) => {
    engine.renameTrack(id, name);
    forceUpdate();
  };

  const onRemove = (id) => {
    engine.removeTrack(id);
    forceUpdate();
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="timeline">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#262626] shrink-0">
        <h3 className="font-display text-sm uppercase tracking-widest text-neutral-300 flex items-center gap-1.5">
          Timeline & Waveform
          <InfoDot section="timeline" label="How Timeline & Tracks work" />
        </h3>
        <div className="flex items-center gap-3">
          <span className="hw-label text-[9px] hidden md:inline">Record mic · import audio · loops with transport</span>
          <button
            data-testid="add-track-btn"
            onClick={addTrack}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[#404040] hover:border-[#00e5ff] hover:text-[#00e5ff] transition-colors text-[10px] hw-label"
          >
            <Plus size={12} /> Add Track
          </button>
        </div>
      </div>
      {/* ruler */}
      <div className="flex border-b border-[#262626] shrink-0">
        <div className="w-40 shrink-0 bg-[#141414] border-r border-[#262626]" />
        <div className="flex-1 flex relative bg-[#0a0a0a] h-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex-1 border-r border-[#222] flex items-center px-2">
              <span className="font-mono-data text-[9px] text-neutral-600">{i + 1}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {trackIds.map((id) => {
          const ch = engine.started ? engine.channels[id] : null;
          return (
            <TrackLane
              key={id}
              id={id}
              label={ch ? ch.name : id.replace("track", "Track ")}
              color={ch ? ch.color : "#a855f7"}
              recTrack={recTrack}
              setRecTrack={setRecTrack}
              onRename={onRename}
              onRemove={onRemove}
            />
          );
        })}
      </div>
    </div>
  );
}
